import React from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, TextInput, Select, MultiSelect } from "@/components";
import type {
  ContentStrategyConfig,
  ContentStrategyStatus,
  ContentStrategyMode,
  ContentStrategyDurationPeriod,
} from "../interfaces";

const DURATION_PERIOD_OPTIONS: { value: ContentStrategyDurationPeriod; label: string }[] =
  [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
    { value: "indefinite", label: "Indefinite" },
  ];

const STATUS_OPTIONS: { value: ContentStrategyStatus; label: string }[] = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
];

const MODE_OPTIONS: { value: ContentStrategyMode; label: string }[] = [
  { value: "AUTO", label: "Auto" },
  { value: "REQUIRE_REVIEW", label: "Require Review" },
];

const AGE_GROUP_OPTIONS = [
  { value: "13-17", label: "13-17" },
  { value: "18-24", label: "18-24" },
  { value: "25-34", label: "25-34" },
  { value: "35-44", label: "35-44" },
  { value: "45-54", label: "45-54" },
  { value: "55-64", label: "55-64" },
  { value: "65+", label: "65+" },
];

const REGION_OPTIONS = [
  { value: "North America", label: "North America" },
  { value: "South America", label: "South America" },
  { value: "Europe", label: "Europe" },
  { value: "Asia Pacific", label: "Asia Pacific" },
  { value: "Middle East", label: "Middle East" },
  { value: "Africa", label: "Africa" },
  { value: "Oceania", label: "Oceania" },
];

const ETHNICITY_OPTIONS = [
  { value: "African American", label: "African American" },
  { value: "Asian", label: "Asian" },
  { value: "Caucasian", label: "Caucasian" },
  { value: "Hispanic/Latino", label: "Hispanic/Latino" },
  { value: "Middle Eastern", label: "Middle Eastern" },
  { value: "Native American", label: "Native American" },
  { value: "Pacific Islander", label: "Pacific Islander" },
  { value: "Other", label: "Other" },
];

const INTEREST_OPTIONS = [
  { value: "Technology", label: "Technology" },
  { value: "Sports", label: "Sports" },
  { value: "Music", label: "Music" },
  { value: "Travel", label: "Travel" },
  { value: "Fashion", label: "Fashion" },
  { value: "Food & Cooking", label: "Food & Cooking" },
  { value: "Health & Fitness", label: "Health & Fitness" },
  { value: "Arts & Culture", label: "Arts & Culture" },
  { value: "Gaming", label: "Gaming" },
  { value: "Reading", label: "Reading" },
  { value: "Entertainment", label: "Entertainment" },
  { value: "Business", label: "Business" },
  { value: "Education", label: "Education" },
  { value: "Lifestyle", label: "Lifestyle" },
  { value: "Photography", label: "Photography" },
];

const configSchema = yup.object({
  status: yup.string().oneOf(["ACTIVE", "DRAFT"]).required(),
  mode: yup.string().oneOf(["AUTO", "REQUIRE_REVIEW"]).required(),
  period: yup.string().oneOf(["daily", "weekly", "monthly", "quarterly", "yearly", "indefinite"]).required(),
  periodCount: yup.number().min(1).required(),
  ageGroups: yup.array().of(yup.string()).optional(),
  ethnicities: yup.array().of(yup.string()).optional(),
  regions: yup.array().of(yup.string()).optional(),
  interests: yup.array().of(yup.string()).optional(),
});

type ConfigFormData = {
  status: ContentStrategyStatus;
  mode: ContentStrategyMode;
  period: ContentStrategyDurationPeriod;
  periodCount: number;
  ageGroups?: string[];
  ethnicities?: string[];
  regions?: string[];
  interests?: string[];
};

interface StrategyConfigStepProps {
  initialConfig?: Partial<ContentStrategyConfig & { status: ContentStrategyStatus; mode: ContentStrategyMode }>;
  onNext: (config: {
    status: ContentStrategyStatus;
    mode: ContentStrategyMode;
    config: ContentStrategyConfig;
  }) => void;
  onBack: () => void;
}

export const StrategyConfigStep: React.FC<StrategyConfigStepProps> = ({
  initialConfig,
  onNext,
  onBack,
}) => {
  const { register, handleSubmit, watch, setValue, control, formState: { errors } } = useForm<ConfigFormData>({
    resolver: yupResolver(configSchema) as any,
    defaultValues: {
      status: initialConfig?.status ?? "DRAFT",
      mode: initialConfig?.mode ?? "REQUIRE_REVIEW",
      period: initialConfig?.duration?.period ?? "weekly",
      periodCount: initialConfig?.duration?.periodCount ?? 1,
      ageGroups: initialConfig?.demographics?.ageGroups ?? [],
      ethnicities: initialConfig?.demographics?.ethnicities ?? [],
      regions: initialConfig?.demographics?.regions ?? [],
      interests: initialConfig?.demographics?.interests ?? [],
    },
  });

  const onFormSubmit = (data: ConfigFormData) => {
    const hasDemographics =
      (data.ageGroups?.length ?? 0) > 0 ||
      (data.regions?.length ?? 0) > 0 ||
      (data.ethnicities?.length ?? 0) > 0 ||
      (data.interests?.length ?? 0) > 0;
    onNext({
      status: data.status,
      mode: data.mode,
      config: {
        demographics: hasDemographics
          ? {
              ageGroups: data.ageGroups,
              regions: data.regions,
              ethnicities: data.ethnicities,
              interests: data.interests,
            }
          : undefined,
        duration: {
          period: data.period,
          periodCount: data.periodCount,
        },
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={watch("status")}
          onChange={(val) => setValue("status", val as ContentStrategyStatus)}
        />
        <Select
          label="Mode"
          options={MODE_OPTIONS}
          value={watch("mode")}
          onChange={(val) => setValue("mode", val as ContentStrategyMode)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Duration Period"
          options={DURATION_PERIOD_OPTIONS}
          value={watch("period")}
          onChange={(val) => setValue("period", val as ContentStrategyDurationPeriod)}
        />
        <TextInput
          label="Duration Count"
          type="number"
          min={1}
          error={!!errors.periodCount}
          helperText={errors.periodCount?.message}
          {...register("periodCount", { valueAsNumber: true })}
        />
      </div>

      <div className="border-t border-input pt-6">
        <h3 className="text-sm font-medium mb-4">Demographics (optional)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            name="ageGroups"
            control={control}
            render={({ field }) => (
              <MultiSelect
                label="Age Groups"
                options={AGE_GROUP_OPTIONS}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder="Select age groups"
              />
            )}
          />
          <Controller
            name="regions"
            control={control}
            render={({ field }) => (
              <MultiSelect
                label="Regions"
                options={REGION_OPTIONS}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder="Select regions"
              />
            )}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Controller
            name="ethnicities"
            control={control}
            render={({ field }) => (
              <MultiSelect
                label="Ethnicities"
                options={ETHNICITY_OPTIONS}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder="Select ethnicities"
              />
            )}
          />
          <Controller
            name="interests"
            control={control}
            render={({ field }) => (
              <MultiSelect
                label="Interests"
                options={INTEREST_OPTIONS}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder="Select interests"
              />
            )}
          />
        </div>
      </div>

      <div className="flex gap-4 justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit">Next</Button>
      </div>
    </form>
  );
};
